import { useEffect, useRef, useState } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { InputFacilityData, OutputFacilityData } from './types';
import {
  COMPACT_FONT_SIZE,
  COMPACT_LABEL_SIZE,
  downloadCSV,
  downloadIcon,
  FULL_FONT_SIZE,
  FULL_LABEL_SIZE,
} from './utils';

type InputObject = {
  total_emissions: Record<string, number>;
  leaking_equipment_count: Record<string, number>;
  equipment_count: Record<string, number>;
  population_ef: Record<string, number>;
  leaker_ef: Record<string, number>;
  equipment_type: Record<string, string>;
};

type OutputRow = [
  string, // Updated Equipment Type
  number | string, // Total No. Equip.
  number | string, // No. Emit. Equip.
  number | string, // Emitted (%)
  number | string, // Emis. Rate (kg/h)
  number | string, // Population EF (kg/h)
  number | string, // Leaker EF (kg/h)
  number | string, // Emission Fraction
];

function transformData(input: InputObject): OutputRow[] {
  const result: OutputRow[] = [
    [
      'Updated Equipment Type',
      'Total No. Equip.',
      'No. Emit. Equip.',
      'Emitted (%)',
      'Emis. Rate (kg/h)',
      'Population EF (kg/h)',
      'Leaker EF (kg/h)',
      'Emission Fraction',
    ],
  ];

  for (const equipmentTypeNum in input.total_emissions) {
    const equipmentType = input.equipment_type[equipmentTypeNum];
    const totalEquip = input.equipment_count[equipmentTypeNum] || 0;
    const leakingEquip = input.leaking_equipment_count[equipmentTypeNum] || 0;
    const totalEmissions = input.total_emissions[equipmentTypeNum] || 0;
    const populationEF = input.population_ef[equipmentTypeNum] || 0;
    const leakerEF = input.leaker_ef[equipmentTypeNum] || 0;

    const emittedPercentage =
      totalEquip > 0 ? (leakingEquip / totalEquip) * 100 : 0;
    const emissionFraction =
      totalEmissions > 0 ? leakingEquip / totalEmissions : 0;

    result.push([
      equipmentType,
      totalEquip,
      leakingEquip,
      emittedPercentage,
      totalEmissions,
      populationEF,
      leakerEF,
      emissionFraction,
    ]);
  }

  return result;
}

function transformFacilityData(input: InputFacilityData): OutputFacilityData {
  const result: OutputFacilityData = [];

  for (const key in input.facility_type) {
    const facilityType = input.facility_type[key];
    const leakerFacility = input.leaker_facility[key];
    const populationFacility = input.population_facility[key];
    const emittedCount = input.emitted_count[key];
    const count = input.count[key];

    if (leakerFacility !== null && emittedCount > 0 && count > 0) {
      result.push({
        'Facility Type': facilityType,
        AverageEmissionRate: leakerFacility,
        EquipmentCount: emittedCount,
        AveragePopulationRate: populationFacility,
        count: count,
      });
    }
  }

  return result;
}

const processLeakerData = (data: void | any[], type: string) => {
  if (!data) return [];

  const order =
    type === 'equip'
      ? [
          'Well',
          'Separator',
          'Tank',
          'Compressor',
          'Flare / Combustor',
          'Dehydrator',
          'Facility Piping',
        ]
      : [
          'Wellpad',
          'Compressor Station',
          'Gathering and Boosting Unit',
          'Gas Processing Plant',
          'Dehydration Facility',
        ];

  const listToRemove =
    type === 'equip'
      ? ['VRU', 'Generator', 'Other']
      : [
          'Coal Mine',
          'Coal Vent',
          'Concentrated Animal Feeding Operation',
          'Conv_Well',
          'Heater',
          'Interconnect',
          'Landfill',
          'Tank Batteries',
          'Meter Site',
          'Underground Gas Storage',
        ];

  const updated = data
    .map((item: any) => {
      let itemType = type === 'equip' ? item[0] : item['Facility Type'];
      if (itemType === 'Flare') {
        if (type === 'equip') {
          item[0] = 'Flare / Combustor';
        } else {
          item['Facility Type'] = 'Flare / Combustor';
        }
      }

      if (!listToRemove.includes(itemType)) {
        return item;
      }
      return undefined;
    })
    .filter((item) => item)
    .sort((a, b) => {
      const orderA = order.indexOf(
        type === 'equip' ? a[0] : a['Facility Type'],
      );
      const orderB = order.indexOf(
        type === 'equip' ? b[0] : b['Facility Type'],
      );

      let newOrder = -1;

      if (orderA === -1) {
        newOrder = 1;
      }

      if (orderA > orderB) {
        newOrder = 1;
      }
      if (orderA < orderB) {
        newOrder = -1;
      }
      if (orderB === -1) {
        newOrder = 1;
      }
      return newOrder;
    });

  return updated;
};

const processPopulationData = (data: void | any[], type: string) => {
  if (!data) return [];

  const order =
    type === 'equip'
      ? [
          'Well',
          'Separator',
          'Tank',
          'Compressor',
          'Flare / Combustor',
          'Dehydrator',
          'Facility Piping',
        ]
      : [
          'Wellpad',
          'Compressor Station',
          'Gathering and Boosting Unit',
          'Gas Processing Plant',
          'Dehydration Facility',
        ];

  const listToRemove =
    type === 'equip'
      ? ['VRU', 'Generator', 'Other']
      : [
          'Coal Mine',
          'Coal Vent',
          'Concentrated Animal Feeding Operation',
          'Conv_Well',
          'Heater',
          'Interconnect',
          'Landfill',
          'Tank Batteries',
          'Meter Site',
          'Underground Gas Storage',
        ];

  const updated = data
    .map((item: any) => {
      let itemType = type === 'equip' ? item[0] : item['Facility Type'];
      if (itemType === 'Flare') {
        if (type === 'equip') {
          item[0] = 'Flare / Combustor';
        } else {
          item['Facility Type'] = 'Flare / Combustor';
        }
      }
      if (!listToRemove.includes(itemType)) {
        return item;
      }
      return undefined;
    })
    .filter((item) => item)
    .sort((a, b) => {
      const orderA = order.indexOf(
        type === 'equip' ? a[0] : a['Facility Type'],
      );
      const orderB = order.indexOf(
        type === 'equip' ? b[0] : b['Facility Type'],
      );

      if (orderA === -1) {
        return 1;
      }
      if (orderA > orderB) {
        return 1;
      }
      if (orderA < orderB) {
        return -1;
      }
      if (orderB === -1) {
        return 1;
      }
      return -1;
    });

  return updated;
};

const Chart = ({
  emissions,
  options,
  compact,
  ci,
}: {
  emissions: any;
  options: any;
  compact?: boolean;
  ci?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const type =
    options.title.toLowerCase().indexOf('leaker') >= 0
      ? 'leaker'
      : 'population';

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  const updateChartOptions = () => {
    if (!chartInstance.current || !emissions || emissions.length === 0) return;

    let settings: (string | number)[];
    let isConfidenceIntervalRequired;
    let leakerLower: { [x: string]: any };
    let leakerUpper: { [x: string]: any };
    let maxLeaker = 0;

    let populationLower: { [x: string]: any };
    let populationUpper: { [x: string]: any };
    let maxPopulation = 0;

    if (options.type === 'equip') {
      settings = [0, 6, 5, 9, 11, 12, 14];
      isConfidenceIntervalRequired = false;
    }
    if (options.type === 'facility') {
      settings = [0, 1, 2, 3, 4, 12, 14];
      isConfidenceIntervalRequired = false;
    }

    const emissions_updated =
      options.type === 'equip' ? transformData(emissions) : [];
    const emissions_facility_updated =
      options.type === 'facility' ? transformFacilityData(emissions) : [];

    const processed_data =
      options.type === 'equip'
        ? options.title.includes('Leaker')
          ? processLeakerData(emissions_updated, options.type)
          : processPopulationData(emissions_updated, options.type)
        : options.title.includes('Leaker')
          ? processLeakerData(emissions_facility_updated, options.type)
          : processPopulationData(emissions_facility_updated, options.type);

    const sliced_data =
      options.type === 'equip' ? processed_data.slice(1) : processed_data;

    const equipmentTypes_initial = sliced_data.map(
      (item: any[]) => item[settings[0] as number],
    );

    const facilityTypes_initial = sliced_data.map((item: any) => {
      return item[Object.keys(item)[settings[0] as number] as string];
    });
    const leakerEF_initial =
      options.type === 'equip'
        ? sliced_data.map((item: any[]) => item[settings[1] as number])
        : sliced_data.map((item: any) => {
            return item[Object.keys(item)[settings[1] as number] as string];
          });
    const populationEF_initial =
      options.type === 'equip'
        ? sliced_data.map((item: any[]) => item[settings[2] as number])
        : sliced_data.map((item: any) => {
            return item[Object.keys(item)[settings[3] as number] as string];
          });

    function sumValuesByUniqueNames(
      names: string[],
      values: number[],
    ): Record<string, number> {
      const result: Record<string, number> = {};

      for (let i = 0; i < names.length; i++) {
        const name = names[i];
        const value = values[i].toFixed(1);

        if (result[name] !== undefined) {
          result[name] += Number(value);
        } else {
          result[name] = Number(value);
        }
      }

      return result;
    }

    const updatedLeakerData = sumValuesByUniqueNames(
      equipmentTypes_initial,
      leakerEF_initial,
    );

    const updatedFacilityLeakerData = sumValuesByUniqueNames(
      facilityTypes_initial,
      leakerEF_initial,
    );

    const updatedFacilityPopulationData = sumValuesByUniqueNames(
      facilityTypes_initial,
      populationEF_initial,
    );

    const updatedPopulationData = sumValuesByUniqueNames(
      equipmentTypes_initial,
      populationEF_initial,
    );

    const equipmentTypes = Object.entries(updatedLeakerData).map(
      (item) => item[0],
    );
    const leakerEF = Object.values(updatedLeakerData);
    const populationEF = Object.values(updatedPopulationData);
    const facilityTypes = Object.keys(updatedFacilityLeakerData);
    const leakerFacilityEF = Object.values(updatedFacilityLeakerData);
    const populationFacilityEF = Object.values(updatedFacilityPopulationData);
    let chartData;
    if (options.type === 'facility') {
      chartData = facilityTypes.map((item: string, index: number) => ({
        name: item,
        value:
          type === 'leaker'
            ? leakerFacilityEF[index]
            : populationFacilityEF[index],
      }));
    }

    if (isConfidenceIntervalRequired) {
      leakerLower =
        options.type === 'equip'
          ? sliced_data.map((item: any[]) => {
              const down = +item[settings[3] as number];
              const full = +item[settings[1] as number];
              return full - down;
            })
          : sliced_data.map((item: any[]) => {
              const down = +item[settings[3] as number];
              const full = +item[settings[1] as number];
              return full - down;
            });
      leakerUpper = sliced_data.map((item: any[]) => {
        const up = +item[settings[4] as number];
        const full = +item[settings[1] as number];
        return up + full;
      });

      populationLower = sliced_data.map((item: any[]) => {
        const down = +item[settings[5] as number];
        const full = +item[settings[2] as number];
        return full - down;
      });
      populationUpper = sliced_data.map((item: any[]) => {
        const up = +item[settings[6] as number];
        const full = +item[settings[2] as number];
        return up + full;
      });

      maxLeaker =
        leakerUpper && Math.round(Math.max(...(leakerUpper as Array<number>)));
      maxPopulation =
        populationUpper &&
        Math.round(Math.max(...(populationUpper as Array<number>)));
    }

    const confidenceIntervalOptions = isConfidenceIntervalRequired
      ? {
          name: 'Confidence Interval',
          type: 'custom',
          itemStyle: {
            color: '#ff0000',
          },
          renderItem: (
            params: any,
            api: {
              value: (arg0: number) => any;
              coord: (arg0: any[]) => any;
              size: (arg0: number[]) => number[];
              visual: (arg0: string) => any;
            },
          ) => {
            const xValue = api.value(0);
            const highPoint = api.coord([xValue, api.value(1)]);
            const lowPoint = api.coord([xValue, api.value(2)]);
            const halfWidth = api.size([1, 0])[0] * 0.1;

            return {
              type: 'group',
              children: [
                {
                  type: 'line',
                  shape: {
                    x1: highPoint[0] - halfWidth,
                    y1: highPoint[1],
                    x2: highPoint[0] + halfWidth,
                    y2: highPoint[1],
                  },
                  style: {
                    stroke: api.visual('color'),
                    fill: null,
                  },
                },
                {
                  type: 'line',
                  shape: {
                    x1: lowPoint[0] - halfWidth,
                    y1: lowPoint[1],
                    x2: lowPoint[0] + halfWidth,
                    y2: lowPoint[1],
                  },
                  style: {
                    stroke: api.visual('color'),
                    fill: null,
                  },
                },
                {
                  type: 'line',
                  shape: {
                    x1: highPoint[0],
                    y1: highPoint[1],
                    x2: lowPoint[0],
                    y2: lowPoint[1],
                  },
                  style: {
                    stroke: api.visual('color'),
                    fill: null,
                  },
                },
              ],
            };
          },
          encode: {
            x: 0,
            y: [9, 11],
          },
          data: emissions
            .slice(1)
            .map((item: any, index: string | number) => [
              index,
              type === 'leaker' ? leakerLower[index] : populationLower[index],
              type === 'leaker' ? leakerUpper[index] : populationUpper[index],
            ]),
        }
      : undefined;

    const insertedNames =
      options.type === 'facility'
        ? chartData && chartData.map((item: { name: any }) => item.name)
        : equipmentTypes;

    const insertedData =
      type === 'leaker'
        ? options.type === 'facility'
          ? chartData && chartData.map((item: { value: any }) => item.value)
          : leakerEF
        : options.type === 'facility'
          ? chartData && chartData.map((item: { value: any }) => item.value)
          : populationEF;

    const option = {
      title: {
        text: options.title,
        left: 'left',

        textStyle: {
          fontSize: !isFullscreen
            ? COMPACT_FONT_SIZE.toString()
            : FULL_FONT_SIZE.toString() + 'px',
        },
      },
      grid: {
        top: compact ? 50 : 60,
        bottom: options.type === 'facility' ? 110 : 110,
        left: compact ? 50 : 50,
        right: compact ? 20 : 30,
      },
      tooltip: {
        trigger: 'axis',
      },
      xAxis: {
        type: 'category',
        data:
          options.type === 'facility'
            ? chartData && chartData.map((item: { name: any }) => item.name)
            : equipmentTypes,
        axisLabel: {
          interval: 0,
          rotate: 90,
          overflow: 'break',
          width: 100,
          fontSize: !isFullscreen ? COMPACT_LABEL_SIZE : FULL_LABEL_SIZE,
          formatter: function (value: string) {
            return value;
            // return value.length > 10 && !isFullscreen
            //   ? value.includes(' ')
            //     ? value.replaceAll(' ', '\n')
            //     : value.match(/.{1,10}/g)?.join('\n')
            //   : value;
          },
        },
      },
      yAxis: {
        type: 'value',
        name: options.yLabel,
        nameRotate: 90,
        position: 'left',
        offset: 0,
        nameLocation: 'middle',
        nameGap: 30,
        max:
          options.type === 'equip'
            ? type === 'leaker'
              ? maxLeaker || null
              : maxPopulation || null
            : null,
      },
      series: [
        {
          name: options.xLabel,
          type: 'bar',
          data:
            type === 'leaker'
              ? options.type === 'facility'
                ? chartData &&
                  chartData.map((item: { value: any }) => item.value)
                : leakerEF
              : options.type === 'facility'
                ? chartData &&
                  chartData.map((item: { value: any }) => item.value)
                : populationEF,
          itemStyle: {
            color: 'rgba(84, 112, 198, 0.6)',
          },
        },
      ],
      toolbox: {
        show: isFullscreen,
        right: '3%',
        feature: {
          saveAsImage: {},
          myDownload: {
            show: true,
            title: 'Download data',
            icon: downloadIcon,
            onclick: function () {
              downloadCSV([insertedNames, insertedData] || []);
            },
          },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    isConfidenceIntervalRequired &&
      ci &&
      confidenceIntervalOptions &&
      option.series.push(confidenceIntervalOptions);

    chartInstance.current.setOption(option);
  };

  useEffect(() => {
    updateChartOptions();

    if (chartInstance.current) {
      chartInstance.current.resize();
    }
  }, [
    emissions,
    isFullscreen,
    type,
    options.title,
    options.type,
    options.xLabel,
    compact,
  ]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <IconButton
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '10px',
          right: isFullscreen ? '24px' : '10px',
          zIndex: 1000,
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
      <div ref={chartRef} className={'chart pie'} />
    </div>
  );
};

export default Chart;
